import produce from 'immer';

import type { Category, FilterByCategory, Issue, IssueByCategory, IssueRevision } from '../../../shared/issuesV2';

import { byGrouping } from './utils';
import type { IssueRevisionWithIssues, IssuesV2BaseStore } from './types';

export type IssuesV2StoreState<C extends Category> = IssuesV2BaseStore<C, FilterByCategory<C>, IssueByCategory<C>>;

/**
 * Handles the common case of when we want to accept 'all' in addition to a
 * predicate on the list.  This function also handles undefined as false for the
 * case when we are pulling a value out of a selection object, like
 * `selection[revisionId]?.selection`.
 */
function allOr<T>(value: 'all' | T | undefined, f: (t: T) => boolean) {
  return value !== undefined && (value === 'all' || f(value));
}

/**
 * Checks if all issues across all revisions and pages are selected.  There must
 * be at least one revision.
 */
export const areAllIssuesSelected = <C extends Category>(
  state: IssuesV2StoreState<C>,
) =>
    state.items.selection === 'all';

/**
 * Checks that every issue on the current page is selected (including "all").
 * There must be at least one revision.
 */
export const isEveryIssueOnPageSelected = <C extends Category>(
  state: IssuesV2StoreState<C>,
) =>
    byGrouping(
      state,
      ({ issues, selection }) =>
        issues.length > 0 && allOr(selection, (selectedIssues) =>
          issues.every(i => selectedIssues[i.id]),
        ),
      ({ revisions: revisionsValue, selection }) => {
        if (!revisionsValue.value || revisionsValue.value.length === 0) {
          return false;
        }
        const revisions = revisionsValue.value;
        return allOr(selection, (selectedRevisions) =>
          revisions.every(r => selectedRevisions[r.revisionId]?.selection === 'all'),
        );
      },
    );

/** Checks if any issues on the current page are selected (including "all"). */
export const isAnyIssueOnPageSelected = <C extends Category>(
  state: IssuesV2StoreState<C>,
) =>
    byGrouping(
      state,
      ({ issues, selection }) =>
        allOr(selection, (selectedIssues) =>
          issues.some(i => selectedIssues[i.id]),
        ),
      ({ revisions: revisionsValue, selection }) => {
        if (!revisionsValue.value) {
          return false;
        }
        const revisions = revisionsValue.value;
        return allOr(selection, (selectedRevisions) =>
          revisions.some(r =>
            allOr(selectedRevisions[r.revisionId]?.selection, (selectedIssues) =>
              r.issues.some(i => selectedIssues[i.id]),
            ),
          ),
        );
      },
    );

/** Calculates the count of total selected issues, across both issue and revision selections. */
export const countSelectedIssues = <C extends Category>(
  state: IssuesV2StoreState<C>,
) =>
    byGrouping(
      state,
      ({ selection }) =>
        selection === 'all'
          ? state.statusSummary?.issues?.[state.status] ?? 0
          : Object.keys(selection).length,
      ({ selection }) =>
        selection === 'all'
          ? state.statusSummary?.revisions?.[state.status] ?? 0
          : Object.values(selection).reduce(
            (acc, r) => acc + (r.selection === 'all' ? r.revision.issueCount : Object.keys(r.selection).length),
            0,
          ),
    );

/**
 * Returns a list of issues that have been individually selected, or 'all' if
 * all are selected. This will not include revisions that are completely
 * selected.
 */
export const getSelectedIssues = <
  C extends Category,
>(state: IssuesV2StoreState<C>): 'all' | Array<IssueByCategory<C>> =>
    byGrouping(
      state,
      ({ selection }) =>
        selection === 'all'
          ? 'all'
          : Object.values(selection),
      ({ selection }) =>
        selection === 'all'
          ? 'all'
          : Object.values(selection).flatMap(revision =>
            revision.selection === 'all'
              ? [] // Return nothing because the revision selection will cover it.
              : Object.values(revision.selection),
          ),
    );

/**
 * Returns a list of revisions that have been individually selected, or 'all' if
 * all are selected.  This will not include issues that are individually
 * selected.
 */
export const getSelectedRevisions = <
  C extends Category,
>(state: IssuesV2StoreState<C>): 'all' | Array<IssueRevisionWithIssues<Array<IssueByCategory<C>>>> =>
    byGrouping(
      state,
      ({ selection }) =>
        selection === 'all'
          ? 'all'
          : [],
      ({ selection }) =>
        selection === 'all'
          ? 'all'
          : Object.values(selection).filter(s => s.selection === 'all').map(s => s.revision),
    );

/** Checks if a specific issue is included in the current selection. */
export const isIssueSelected = <
  C extends Category,
>(state: IssuesV2StoreState<C>, issue: Issue) =>
    byGrouping(
      state,
      ({ selection }) =>
        allOr(selection, selectedIssues => selectedIssues[issue.id] !== undefined),
      ({ selection }) =>
        allOr(selection, selectedRevisions =>
          allOr(selectedRevisions[issue.source.id]?.selection, selectedIssues =>
            selectedIssues[issue.id] !== undefined)),
    );

/**
 * Checks if a specific revision is selected in its entirety.  This is true only
 * if and only iff all the issues for the revision matching the current filter
 * are considered selected.
 */
export const isRevisionSelected = <
  C extends Category,
>(state: IssuesV2StoreState<C>, revision: IssueRevision) =>
    byGrouping(
      state,
      () => false,
      ({ selection }) => allOr(selection, selectedRevisions => selectedRevisions[revision.revisionId]?.selection === 'all'),
    );

/** Checks if any issue belonging to a revision is selected. */
export const isAnyIssueForRevisionSelected = <
  C extends Category,
>(state: IssuesV2StoreState<C>, revision: IssueRevision) =>
    byGrouping(
      state,
      () => false,
      ({ selection }) => allOr(selection, selectedRevisions =>
        allOr(selectedRevisions[revision.revisionId]?.selection, (selectedIssues) =>
          Object.keys(selectedIssues).length > 0,
        ),
      ),
    );

/** Selects an issue, returning a new copy of the state. */
export const selectIssue = <
  C extends Category,
  I extends IssueByCategory<C>,
>(rawState: IssuesV2StoreState<C>, issue: I): IssuesV2StoreState<C> =>
    produce(rawState, draftState => {
      if (isIssueSelected(draftState, issue)) {
        return;
      }

      byGrouping(
        draftState,
        (draftItems) => {
          if (draftItems.selection === 'all') {
            return;
          }
          draftItems.selection[issue.id] = issue;
          if (draftState.statusSummary?.issues?.[draftState.status] === Object.keys(draftItems.selection).length) {
            draftItems.selection = 'all';
          }
        },
        (draftItems) => {
          if (draftItems.selection === 'all') {
            return;
          }

          const draftSelection = draftItems.selection;

          const revision = draftItems.revisions.value?.find(r => r.revisionId === issue.source.id);
          if (!revision) {
            return;
          }

          let revisionSelection = draftSelection[revision.revisionId];

          if (revisionSelection === undefined) {
            revisionSelection = {
              revision,
              selection: {},
            };
          }

          if (revisionSelection.selection === 'all') {
            return;
          }

          revisionSelection.selection[issue.id] = issue;

          if (revision.issueCount === Object.keys(revisionSelection.selection).length) {
            revisionSelection.selection = 'all';
          }

          draftSelection[revision.revisionId] = revisionSelection;

          // TODO: If all the revisions are selected, select everything
          // This needs to know the total number of revisions.
        },
      );
    });

/** De-selects an issue, returning a new copy of the state. */
export const deselectIssue = <
  C extends Category,
>(rawState: IssuesV2StoreState<C>, issue: IssueByCategory<C>): IssuesV2StoreState<C> =>
    produce(rawState, draftState => {
      if (!isIssueSelected(draftState, issue)) {
        return;
      }

      byGrouping(
        draftState,
        (draftItems) => {
          if (draftItems.selection === 'all') {
            draftItems.selection = Object.fromEntries(draftItems.issues.map(i => [i.id, i]));
          }

          delete draftItems.selection[issue.id];
        },
        (draftItems) => {
          if (!draftItems.revisions.value) {
            return;
          }
          const revision = draftItems.revisions.value.find(r => r.revisionId === issue.source.id);
          if (!revision) {
            return;
          }

          // If all revisions are selected, break them out.
          if (draftItems.selection === 'all') {
            draftItems.selection = Object.fromEntries(draftItems.revisions.value.map(r => [r.revisionId, { revision: r, selection: 'all' }]));
          }

          const revisionSelection = draftItems.selection[revision.revisionId];

          // Something weird happened if we are deselecting an issue that's not for a revision with selections on the page.
          if (!revisionSelection) {
            return;
          }

          // If all the issues for this revision are selected, break them out.
          if (revisionSelection.selection === 'all') {
            revisionSelection.selection = Object.fromEntries(revision.issues.map(i => [i.id, i]));
          }

          delete revisionSelection.selection[issue.id];
        },
      );
    });

/** Selects a revision and all it's issues, returning a new copy of the state. */
export const selectRevision = <
  C extends Category,
  I extends IssueByCategory<C>,
  >(
    rawState: IssuesV2StoreState<C>,
    revision: IssueRevisionWithIssues<I[]>,
  ): IssuesV2StoreState<C> =>
    produce(rawState, draftState => {
      if (isRevisionSelected(rawState, revision)) {
        return;
      }

      byGrouping(
        draftState,
        () => {
        // Do nothing if grouped by issue.
        },
        (draftItems) => {
          if (draftItems.selection !== 'all') {
            draftItems.selection[revision.revisionId] = {
              revision,
              selection: 'all',
            };
          }

          // TODO: If all the revisions are selected, select everything
          // This needs to know the total number of revisions.
        },
      );
    });

/** Deselects a revision so that none of it's issues will be selected. */
export const deselectRevision = <
  C extends Category,
>(
    rawState: IssuesV2StoreState<C>,
    revision: IssueRevision,
  ): IssuesV2StoreState<C> =>
    produce(rawState, draftState => {
      if (!isAnyIssueForRevisionSelected(rawState, revision)) {
        return;
      }

      byGrouping(
        draftState,
        () => {
        // Do nothing if grouped by issue.
        },
        (draftItems) => {
          if (!draftItems.revisions.value) {
            return;
          }
          if (draftItems.selection === 'all') {
            draftItems.selection = Object.fromEntries(draftItems.revisions.value.map(r => [r.revisionId, { revision: r, selection: 'all' }]));
          }
          delete draftItems.selection[revision.revisionId];
        },
      );
    });

/** Selects all issues across all pages and revisions. */
export const selectAll = <
  C extends Category,
>(rawState: IssuesV2StoreState<C>): IssuesV2StoreState<C> =>
    produce(rawState, draftState => {
      draftState.items.selection = 'all';
    });

/** Clears the selection, selecting nothing. */
export const clearSelection = <
  C extends Category,
>(rawState: IssuesV2StoreState<C>): IssuesV2StoreState<C> =>
    produce(rawState, draftState => {
      draftState.items.selection = {};
    });
